import type { RoomRateSelection } from '../surcharges/surchargeUtils';

/** One bucket for every room rate whose rate plan is missing. */
export const UNKNOWN_RATE_PLAN_ID = 'unknown-rate-plan';
export const UNKNOWN_RATE_PLAN_NAME = 'Unknown rate plan';

export interface EgsRatePlan {
  id: string;
  name: string;
  roomRateCount: number;
}

// Ids, not names: a property can carry two plans under one name, and only the id is safe to key on.
export function getRatePlanId(selection: RoomRateSelection): string {
  const ratePlanId = selection.roomRate.rate_plan?.id;

  return ratePlanId === undefined ? UNKNOWN_RATE_PLAN_ID : String(ratePlanId);
}

export function getRatePlanName(selection: RoomRateSelection): string {
  return selection.roomRate.rate_plan?.name ?? UNKNOWN_RATE_PLAN_NAME;
}

/** The plans this offer's room rates sit on, which is what the wizard scopes itself to on the first step. */
export function buildEgsRatePlans(roomRateSelections: Array<RoomRateSelection>): Array<EgsRatePlan> {
  const plans = new Map<string, EgsRatePlan>();

  roomRateSelections.forEach((selection) => {
    const id = getRatePlanId(selection);
    const plan = plans.get(id) ?? { id, name: getRatePlanName(selection), roomRateCount: 0 };

    plan.roomRateCount += 1;
    plans.set(id, plan);
  });

  return Array.from(plans.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function filterSelectionsByRatePlan(
  roomRateSelections: Array<RoomRateSelection>,
  ratePlanId: string | undefined,
): Array<RoomRateSelection> {
  if (!ratePlanId) {
    return [];
  }

  return roomRateSelections.filter((selection) => getRatePlanId(selection) === ratePlanId);
}

/**
 * Auto-selected only where there is nothing to choose: with two plans in play, defaulting to a guess lets a
 * CM click past the question straight into the wrong plan's prices.
 */
export function getDefaultEgsRatePlanId(ratePlans: Array<EgsRatePlan>): string | undefined {
  return ratePlans.length === 1 ? ratePlans[0].id : undefined;
}
